import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import queryString from 'query-string'
import { withRadium, SearchParams, DetectCompact, gtmEvent } from 'higher-order-components'
import { cacheKeyGen } from 'apis/outlet'
import { selectors as environmentSelectors } from 'store/actions/environment'
import COLOR_PALETTE from 'constants/color-palette'
import { bindAll, get } from 'utils'
import { LoaderIcon, Button } from 'components'

import { actionCreators, selectors } from 'store/actions/outlet'
import { bindActionCreators } from 'redux'
import { gtmOutletStoreActionTypes } from 'store/gtm-events'
import { WINDOW } from 'constants/environment'

import NavigationBar from './navigation-bar'
import SelectNameplate from './select-nameplate'
import SelectDealer from './select-dealer'
import ShowResults from './show-results'

class OutletStoreContainer extends PureComponent {
  constructor(props) {
    super(props)
    this.state = {
      navHeight: 0,
      vehicleNotAvailableError: 0
    }
    bindAll(
      [
        'nameplateChange',
        'dealerChange',
        'updateSearchParams',
        'stepChange',
        'handleNameplateClick',
        'sortedResults',
        'handleFilterChange',
        'handleSortChange',
        'changeStep'
      ],
      this
    )
  }
  componentWillMount() {
    const { outletStockEndpoint } = this.props
    this.props.initOutletStore({ outletStockEndpoint })
  }

  componentDidMount() {
    this.setState({ navHeight: this.navBar.clientHeight })
    this.props.search.pushQuery({
      ...this.props.search.query,
      previousDealer: undefined // clear off previous Dealer from url (already in redux)
    })
    // Check if there is an error param
    if (get('search.query.error')(this.props)) {
      // If there is, get the error and set to state for displaying error message,
      // then remove it to prevent further error displaying in the next actions
      this.setState({ vehicleNotAvailableError: get('search.query.error')(this.props) }, () =>
        this.props.search.pushQuery({
          ...this.props.search.query,
          ...{ error: undefined }
        }))
    }
  }

  componentWillReceiveProps(nextProps) {
    const onPropChange = (path, expose, action) => {
      const value = get(path)
      if (value(nextProps) !== value(this.props)) {
        action({ [expose]: value(nextProps) })
      }
    }
    onPropChange('search.query.nameplate', 'nameplate', this.nameplateChange)
    onPropChange('search.query.dealerCode', 'dealerCode', this.dealerChange)
    onPropChange('search.query.locationId', 'id', this.dealerChange)

    onPropChange('outletState.nameplate', 'nameplate', this.updateSearchParams)
    onPropChange('outletState.dealer.dealerCode', 'dealerCode', this.updateSearchParams)
    onPropChange('outletState.dealer.id', 'locationId', this.updateSearchParams)
    onPropChange('search.query.error', 'error', this.updateSearchParams)

    onPropChange('outletState.step', 'step', this.stepChange)
    onPropChange('search.query.step', 'stepIndex', this.stepChange)
  }

  filterDataMapping = (filter) => {
    switch (filter) {
      case 'DEMO':
        return 0
      case 'RUNOUT':
        return 1
      default:
        return 0
    }
  }

  filterCategory = (filters = []) => result =>
    result && filters.some(filter => this.filterDataMapping(filter) === result.storeType)

  stepChange({ step, stepIndex, context }) {
    const { stepChange } = this.props
    const steps = ['SELECT_DEALER', 'SELECT_NAMEPLATE', 'SHOW_RESULTS']
    if (step) {
      this.updateSearchParams({ step: steps.indexOf(step) })
    } else {
      stepChange({ step: steps[stepIndex], context })
    }
    WINDOW && WINDOW.scrollTo(0, this.props.globalHeaderHeight)
  }

  nameplateChange({ nameplate }) {
    this.props.nameplateChange({ nameplate })
  }

  handleNameplateClick({ nameplate }) {
    const { stepChange, outletState } = this.props
    this.nameplateChange({ nameplate })
    this.props.gtmEvent({
      gtmID: gtmOutletStoreActionTypes.SELECT_NAMEPLATE,
      gtmPayload: { nameplate, dealerName: get('dealer.name', outletState) }
    })
    stepChange({ step: 'SHOW_RESULTS' })
  }

  dealerChange({ dealerCode, id }) {
    const { dealerChangeRequest } = this.props
    dealerChangeRequest({ dealerCode, id })
  }

  updateSearchParams(param) {
    this.props.search.pushParam(param)
  }

  changeStep(stepID) {
    const { dealerReset, stepChange } = this.props
    switch (stepID) {
      case 'SELECT_DEALER':
        dealerReset()
        stepChange({ step: 'SELECT_DEALER', copyMode: 'CHANGE' })
        break
      case 'SELECT_NAMEPLATE':
        stepChange({
          step: 'SELECT_NAMEPLATE',
          copyMode: 'CHANGE',
          nameplate: false
        })
        break
      default:
        return null
    }
  }

  handleFilterChange({ key, checked }) {
    const { updateFilters, outletState: { filter } } = this.props
    let updatedFilters
    if (!checked) {
      updatedFilters = filter.filter(filter => filter !== key)
    } else {
      updatedFilters = [...filter, key]
    }
    const deDupedFilters = updatedFilters.filter((item, index, self) => self.indexOf(item) === index)
    updateFilters(deDupedFilters)
  }

  handleSortChange({ key, dir, label }) {
    const { updateSort } = this.props
    updateSort({
      key,
      dir,
      gtmID: gtmOutletStoreActionTypes.CLICK_SORT_OPTION,
      gtmPayload: { label }
    })
  }

  sortedResults() {
    const { outletState: { sort, filter, results = [] }, bestMatchMode } = this.props

    // Sort the stocklist by key, if those values of given key are the
    // same, sort by price
    const sortKey = key => (result1, result2) => {
      if (result1[key] === result2[key]) {
        return result1.price - result2.price
      }
      return result1[key] - result2[key]
    }

    const sortCategory = (items, order = ['DEMO', 'RUNOUT']) =>
      order.map(this.filterDataMapping).reduce((all, category) => ([
        ...all,
        ...items.filter(item => item.storeType === category)
      ]), [])

    const filteredResults = results.filter(this.filterCategory(filter))

    const sortBestMatch = items => {
      switch (bestMatchMode.toLowerCase()) {
        case 'demo-first':
          return sortCategory(items, ['DEMO', 'RUNOUT'])
        case 'clearance-first':
          return sortCategory(items, ['RUNOUT', 'DEMO'])
        default:
          return items
      }
    }

    switch (sort.dir) {
      case 'BMATCH':
        return sortBestMatch(filteredResults.sort(sortKey(sort.key)).reverse())
      case 'ASC':
        return filteredResults.sort(sortKey(sort.key))
      case 'DESC':
        return filteredResults.sort(sortKey(sort.key)).reverse()
      default:
        return filteredResults
    }
  }

  generateHref = (stock) => {
    const { outletState, detailUrl } = this.props
    const params = {
      stockNumber: stock.stockNumber,
      dealerCode: outletState.dealer.dealerCode,
      locationId: outletState.dealer.id,
      nameplate: stock.nameplate
    }
    return {
      ...stock,
      href: `${detailUrl}?${queryString.stringify(params)}`
    }
  }

  addExtraLabel = (stock) => {
    const {
      showResults: { accessoryWithPremiumTrayIncludedLabel, accessoryIncludedLabel }
    } = this.props
    return {
      ...stock,
      extraInfo: stock.accessories.length
        ? [
          stock.isPremiumTrayIncluded
            ? accessoryWithPremiumTrayIncludedLabel
            : accessoryIncludedLabel
        ]
        : []
    }
  }

  gtmTags = stock => ({
    ...stock,
    gtmEvents: {
      click: [
        {
          func: this.props.gtmEvent,
          args: [
            {
              gtmID: gtmOutletStoreActionTypes.SELECT_STOCK_TILE,
              gtmPayload: {
                ...stock,
                fittedAccessories: stock.accessories.length
                  ? 'Fitted with Accessories'
                  : 'No Accessories',
                dealerName: get('outletState.dealer.name', this.props)
              }
            }
          ]
        }
      ],
      disclaimer: [
        {
          func: this.props.gtmEvent,
          args: [
            {
              gtmID: gtmOutletStoreActionTypes.CLICK_PRICE_DISCLAIMER,
              gtmPayload: stock
            }
          ]
        }
      ]
    }
  })

  addBackgroundImage = (stock) => {
    const { stockTileBackgroundUrl } = this.props
    return { ...stock, backgroundImage: stockTileBackgroundUrl }
  }

  featuredResults() {
    const { outletState: { results = [] } } = this.props
    return results.filter(({ featured }) => featured)
  }

  generateStockTileProps(tiles) {
    return tiles
      .map(this.generateHref)
      .map(this.gtmTags)
      .map(this.addBackgroundImage)
      .map(this.addExtraLabel)
  }

  filterCount(filter) {
    const { outletState: { results = [] } } = this.props
    return results.filter(this.filterCategory([filter])).length
  }

  render() {
    const {
      globalHeaderHeight,
      selectNameplate,
      selectDealer,
      showResults,
      nameplates,
      outletState,
      compact,
      noStockErrorMessage,
      noResultsFoundMessage
    } = this.props

    const nameplateStock = nameplates.map((stock) => {
      const { availability = [] } = outletState
      const stockData = availability.find(({ nameplate }) => nameplate === stock.nameplate)
      return {
        ...stock,
        ...stockData,
        gtmEvents: stockData &&
          !stockData.inStock && [
            {
              func: this.props.gtmEvent,
              args: {
                gtmID: gtmOutletStoreActionTypes.CLICK_NO_STOCK_BUILD,
                gtmPayload: {
                  dealerName: get('dealer.name', outletState),
                  nameplate: stock.nameplate,
                  buttonText: stock.unavailableCta.label
                }
              }
            }
          ]
      }
    })

    const selectedStock = nameplateStock.find(({ nameplate }) => nameplate === outletState.nameplate)

    const getpreHeading = () => {
      const { outletHeading, welcomeHeading } = this.props
      switch (outletState.copyMode) {
        case 'WELCOME':
          return welcomeHeading
        case 'NORMAL':
          return outletHeading
        default:
          return outletHeading
      }
    }

    const getDescription = (step) => {
      const { welcomeDescription, description, changeDescription } = step
      switch (outletState.copyMode) {
        case 'WELCOME':
          return welcomeDescription
        case 'NORMAL':
          return description
        case 'CHANGE':
          return changeDescription
        default:
          return description
      }
    }

    const getHeading = (step) => {
      const { heading, changeHeading } = step
      switch (outletState.copyMode) {
        case 'NORMAL':
          return heading
        case 'CHANGE':
          return changeHeading
        default:
          return heading
      }
    }

    const noStockMessage = (dealer, nameplate) => {
      const key = get(cacheKeyGen(dealer, nameplate))
      const message =
        noStockErrorMessage || `Sorry, there's no ${nameplate} stock available at this dealer. `
      return key(outletState.noResultMessages) && message
    }
    const NoStockMessage = ({
      dealer, cta, nameplate, message, onClick
    }) => (
        <span {...{ onClick }}>
          {message}
          <strong
            style={{
              fontWeight: '500',
              cursor: 'pointer',
              textDecoration: 'underline',
              display: 'block'
            }}>
            {cta} &gt;
          </strong>
        </span>
      )

    const noStockChangeModelMessage = (dealer, nameplate) => {
      const message = noStockMessage(dealer, nameplate)
      return (
        message && (
          <NoStockMessage
            {...{ dealer, nameplate, message }}
            cta={'Change model'}
            onClick={() => {
              this.props.gtmEvent({
                gtmID: gtmOutletStoreActionTypes.CLICK_NO_STOCK_CHANGE,
                gtmPayload: {
                  dealerName: get('name', dealer),
                  nameplate,
                  buttonText: 'Change model'
                }
              })
              this.props.dealerConfirm()
              this.changeStep('SELECT_NAMEPLATE')
            }} />
        )
      )
    }

    const noStockChangeDealerMessage = (dealer, nameplate) => {
      const message = noStockMessage(dealer, nameplate)
      return message ? (
        <NoStockMessage
          {...{ dealer, nameplate, message }}
          cta={'Change dealer'}
          onClick={() => {
            this.props.gtmEvent({
              gtmID: gtmOutletStoreActionTypes.CLICK_NO_STOCK_CHANGE,
              gtmPayload: {
                dealerName: get('name', dealer),
                nameplate,
                buttonText: 'Change dealer'
              }
            })
            this.changeStep('SELECT_DEALER')
          }} />
      ) : null
    }

    const step = (stepID) => {
      switch (stepID) {
        case 'SELECT_DEALER':
          return (
            <SelectDealer
              preHeading={getpreHeading()}
              description={getDescription(selectDealer)}
              heading={getHeading(selectDealer)}
              mapBlockProps={{
                ...selectDealer.mapBlockProps,
                gtmMapping: gtmOutletStoreActionTypes,
                searchOverride: outletState.previousDealer,
                onDealerChange: ({ selected }) => {
                  if (!selected) return
                  if (
                    selected.dealerCode === outletState.dealer.dealerCode &&
                    selected.id === outletState.dealer.id
                  ) { return }
                  this.dealerChange(selected)
                },
                mapLocationData: (location) => {
                  const key = get(cacheKeyGen(location, outletState.nameplate))
                  const noStock = key(outletState.noResultMessages)
                  return {
                    ...location,
                    message: noStock && noStockChangeModelMessage(location, outletState.nameplate),
                    selectCTA:
                      !noStock &&
                      (outletState.isFetchingStock ? (
                        <LoaderIcon />
                      ) : (
                          <Button
                            display={compact ? 'block' : 'inline'}
                            onClick={(e) => {
                              this.props.dealerConfirm()
                              e && e.stopPropagation()
                            }}
                            label={'Select Dealer'} />
                        ))
                  }
                }
              }}
              components={outletState.copyMode === 'CHANGE' ? [] : selectDealer.headerComponents}
              {...extra} />
          )
        case 'SELECT_NAMEPLATE':
          return (
            <SelectNameplate
              preHeading={getpreHeading()}
              description={getDescription(selectNameplate)}
              heading={getHeading(selectNameplate)}
              components={!(outletState.copyMode === 'CHANGE') && selectNameplate.headerComponents}
              onNameplateChange={this.handleNameplateClick}
              nameplateStock={nameplateStock}
              noStockError={noStockChangeDealerMessage(outletState.dealer, outletState.nameplate)}
              {...extra} />
          )
        case 'SHOW_RESULTS':
          return (
            <ShowResults
              preHeading={getpreHeading()}
              description={getDescription(showResults)}
              pageDisclaimer={get('pageDisclaimer', showResults)}
              priceDisclaimer={get('priceDisclaimer', showResults)}
              heading={`${showResults.heading} ${selectedStock && selectedStock.nameplate}`}
              components={showResults.footerComponents}
              showLoader={outletState.isFetchingStock}
              dealerState={outletState.dealer.state}
              filters={[
                {
                  label: 'Clearance',
                  count: this.filterCount('RUNOUT'),
                  key: 'RUNOUT',
                  color: "blue",
                  checked: outletState.filter.includes('RUNOUT'),
                  onClick: this.handleFilterChange,
                  gtmEvents: {
                    checked: [
                      {
                        func: this.props.gtmEvent,
                        args: {
                          gtmID: gtmOutletStoreActionTypes.CLEARANCE_CHECKED,
                          gtmPayload: { label: 'Clearance' }
                        }
                      }
                    ],
                    unchecked: [
                      {
                        func: this.props.gtmEvent,
                        args: {
                          gtmID: gtmOutletStoreActionTypes.CLEARANCE_UNCHECKED,
                          gtmPayload: { label: 'Clearance' }
                        }
                      }
                    ]
                  }
                },
                {
                  label: 'Demo',
                  count: this.filterCount('DEMO'),
                  key: 'DEMO',
                  color: "orange",
                  checked: outletState.filter.includes('DEMO'),
                  onClick: this.handleFilterChange,
                  gtmEvents: {
                    checked: [
                      {
                        func: this.props.gtmEvent,
                        args: {
                          gtmID: gtmOutletStoreActionTypes.DEMO_CHECKED,
                          gtmPayload: { label: 'Demo' }
                        }
                      }
                    ],
                    unchecked: [
                      {
                        func: this.props.gtmEvent,
                        args: {
                          gtmID: gtmOutletStoreActionTypes.DEMO_UNCHECKED,
                          gtmPayload: { label: 'Demo' }
                        }
                      }
                    ]
                  }
                }
              ]}
              sortOptions={[
                {
                  label: 'Best Match',
                  key: 'price',
                  dir: 'BMATCH'
                },
                {
                  label: 'Price High to Low',
                  key: 'price',
                  dir: 'DESC'
                },
                {
                  label: 'Price Low to High',
                  key: 'price',
                  dir: 'ASC'
                }
              ]}
              handleSortChange={this.handleSortChange}
              featuredStockList={{
                stockList: this.generateStockTileProps(this.featuredResults()),
                heading: 'Featured by',
                dealerName: outletState.dealer.name
              }}
              stockList={this.generateStockTileProps(this.sortedResults())}
              {...extra}
              vehicleNotAvailableError={this.state.vehicleNotAvailableError}
              nameplate={selectedStock && selectedStock.nameplate}
              noResultsFoundMessage={noResultsFoundMessage} />
          )
        default:
          return null
      }
    }
    const { navHeight } = this.state
    const extra = { compact, globalHeaderHeight }
    return (
      <div style={[{ minHeight: `calc(100vh - ${navHeight}px)` }, styles.main(outletState.step)]}>
        {outletState.storeLoading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              margin: '2em 0'
            }}>
            <LoaderIcon />
          </div>
        ) : (
            step(outletState.step)
          )}
        <NavigationBar
          innerRef={(element) => {
            this.navBar = element
          }}
          gtmEvents={{
            editDealer: [
              {
                func: this.props.gtmEvent,
                args: [
                  {
                    gtmID: gtmOutletStoreActionTypes.EDIT_DEALER,
                    gtmPayload: {
                      buttonText: 'Edit',
                      dealerName: get('dealer.name', outletState) || 'Not Selected'
                    }
                  }
                ]
              }
            ],
            editNameplate: [
              {
                func: this.props.gtmEvent,
                args: [
                  {
                    gtmID: gtmOutletStoreActionTypes.EDIT_MODEL,
                    gtmPayload: {
                      buttonText: 'Edit',
                      nameplate: get('nameplate', outletState) || 'Not Selected'
                    }
                  }
                ]
              }
            ]
          }}
          dealerButton={{
            disabled: outletState.step === 'SELECT_DEALER',
            active: outletState.step === 'SELECT_DEALER',
            selectionLabel: 'Step 1',
            selectedText:
              outletState.dealer && outletState.dealerConfirm
                ? get('dealer.name', outletState)
                : '',
            fallbackText: outletState.copyMode === 'CHANGE' ? 'Change dealer' : 'Select dealer',
            onClick: () => {
              this.changeStep('SELECT_DEALER')
            }
          }}
          nameplateButton={{
            disabled: outletState.step !== 'SHOW_RESULTS',
            active: outletState.step === 'SELECT_NAMEPLATE',
            selectionLabel: 'Step 2',
            selectedText: selectedStock ? selectedStock.nameplate : '',
            image: selectedStock ? selectedStock.imageSrcFront : '',
            fallbackText: outletState.copyMode === 'CHANGE' ? 'Change model' : 'Select model',
            onClick: () => {
              this.changeStep('SELECT_NAMEPLATE')
            }
          }}
          showLoader={outletState.isFetchingStock || outletState.isFetchingDealer}
          hint={
            outletState.step === 'SHOW_RESULTS'
              ? {
                label: showResults.hintLabel,
                disclaimer: showResults.hintCopy,
                gtmEvents: [
                  {
                    func: this.props.gtmEvent,
                    args: [
                      {
                        gtmID: gtmOutletStoreActionTypes.CLICK_INFO,
                        gtmPayload: { buttonText: showResults.hintLabel }
                      }
                    ]
                  }
                ]
              }
              : null
          }
          {...globalHeaderHeight}
          step={outletState.step} />
      </div>
    )
  }
}

const styles = {
  main: (step) => {
    if (step === 'SELECT_DEALER') {
      return {
        display: 'flex',
        flexDirection: 'column'
      }
    }
  }
}

const mapStateToProps = state => ({
  outletState: selectors.getOutlet(state)
})

const mapDispatchToProps = dispatch => bindActionCreators(actionCreators, dispatch)

export default withRadium(
  gtmEvent(),
  connect(mapStateToProps, mapDispatchToProps),
  connect(state => ({
    globalHeaderHeight: environmentSelectors.getGlobalHeaderHeight(state)
  })),
  DetectCompact(),
  SearchParams({
    expose: 'search',
    exposeParams: ['nameplate', 'dealerCode', 'step', 'locationId', 'error'],
    trackParams: ['step']
  })
)(OutletStoreContainer)

OutletStoreContainer.propTypes = {
  globalHeaderHeight: PropTypes.number,
  outletStockEndpoint: PropTypes.string,
  stockTileBackgroundUrl: PropTypes.string,
  detailUrl: PropTypes.string,
  search: PropTypes.object,
  nameplates: PropTypes.array,
  outletState: PropTypes.object,
  initOutletStore: PropTypes.func,
  dealerReset: PropTypes.func,
  dealerConfirm: PropTypes.func,
  stepChange: PropTypes.func,
  updateFilters: PropTypes.func,
  updateSort: PropTypes.func,
  gtmEvent: PropTypes.func,
  nameplateChange: PropTypes.func,
  dealerChangeRequest: PropTypes.func,
  outletHeading: PropTypes.string,
  welcomeHeading: PropTypes.string,
  compact: PropTypes.bool,
  noStockErrorMessage: PropTypes.string,
  noResultsFoundMessage: PropTypes.string,
  selectNameplate: PropTypes.shape({
    heading: PropTypes.string,
    changeHeading: PropTypes.string,
    welcomeDescription: PropTypes.string,
    description: PropTypes.string,
    changeDescription: PropTypes.string,
    headerComponents: PropTypes.array
  }),
  selectDealer: PropTypes.shape({
    heading: PropTypes.string,
    changeHeading: PropTypes.string,
    welcomeDescription: PropTypes.string,
    description: PropTypes.string,
    changeDescription: PropTypes.string,
    mapBlockProps: PropTypes.object,
    headerComponents: PropTypes.array
  }),
  showResults: PropTypes.shape({
    hintLabel: PropTypes.string,
    hintCopy: PropTypes.string,
    heading: PropTypes.string,
    welcomeDescription: PropTypes.string,
    description: PropTypes.string,
    footerComponents: PropTypes.array,
    accessoryWithPremiumTrayIncludedLabel: PropTypes.string,
    accessoryIncludedLabel: PropTypes.string
  }),
  bestMatchMode: PropTypes.oneOf(['Clearance-first', 'Demo-first'])
}

OutletStoreContainer.defaultProps = {
  search: {},
  outletState: {},
  outletActions: {},
  bestMatchMode: 'Clearance-first'
}



// WEBPACK FOOTER //
// ./src/containers/outlet-store/index.jsx